import { useEffect, useRef } from 'react'
import images from '@assets/images'
import './BedroomModal.css'

const BEDROOM_CONFIG = {
  '1PN': {
    label: '1 Phòng Ngủ',
    image: images.bedroom1PN,
  },
  '1PN+': {
    label: '1 Phòng Ngủ + 1',
    image: images.bedroom1PNPlus,
  },
  '1PN_GOC': {
    label: '1 Phòng Ngủ + Góc',
    image: images.bedroom1PNGoc,
  },
  '2PN': {
    label: '2 Phòng Ngủ',
    image: images.bedroom2PN,
  },
  '2PN_2': {
    label: '2 Phòng Ngủ Loại 2',
    image: images.bedroom2PN2,
  },
}

/**
 * BedroomModal — modal căn hộ
 * Desktop: 2 cột (info trái, ảnh phải)
 * Tablet:  side panel trượt từ phải (360px)
 * Mobile:  bottom sheet trượt từ dưới, swipe-down để đóng
 */
function BedroomModal({ apt, onClose }) {
  const touchStartY = useRef(0)

  useEffect(() => {
    if (!apt) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [apt, onClose])

  if (!apt) return null

  const config = BEDROOM_CONFIG[apt.bedroomType] ?? {
    label: apt.bedroomType ?? 'Căn hộ',
    image: images.bedroom1PNPlus,
  }

  const formatArea = (val) =>
    typeof val === 'number' ? val.toFixed(2).replace('.', ',') : val

  const towerLabel = apt.block === 'A' ? 'Tháp A' : 'Tháp B'

  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY
  }

  const handleTouchEnd = (e) => {
    const delta = e.changedTouches[0].clientY - touchStartY.current
    if (delta > 80) onClose()
  }

  return (
    <div
      className="bedroom-modal__backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={`Mặt bằng ${config.label}`}
      onClick={onClose}
    >
      {/* DOM order: handle → info → img-wrap
          Desktop (flex-row): (handle hidden) | info | img-wrap
          Mobile  (flex-col + CSS order): handle(1) | img-wrap(2) | info(3) */}
      <div
        className="bedroom-modal__card"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag handle — mobile/tablet only */}
        <div className="bedroom-modal__handle" aria-hidden="true" />

        {/* Info column — left on desktop, bottom on mobile */}
        <div className="bedroom-modal__info">
          {/* Mobile/tablet header: pill + name·tower */}
          <div className="bedroom-modal__mobile-header">
            <span className="bedroom-modal__type-pill">{config.label}</span>
            <p className="bedroom-modal__mobile-title">{apt.maCan} · {towerLabel}</p>
          </div>

          {/* Desktop title */}
          <h2 className="bedroom-modal__info-title">{config.label}</h2>

          <dl className="bedroom-modal__specs">
            <div className="bedroom-modal__spec-row bedroom-modal__spec-row--meta">
              <dt className="bedroom-modal__spec-label">Mã căn</dt>
              <dd className="bedroom-modal__spec-value">{apt.maCan}</dd>
            </div>
            <div className="bedroom-modal__spec-row bedroom-modal__spec-row--meta">
              <dt className="bedroom-modal__spec-label">Tháp</dt>
              <dd className="bedroom-modal__spec-value">{towerLabel}</dd>
            </div>
            <div className="bedroom-modal__spec-row bedroom-modal__spec-row--area">
              <dt className="bedroom-modal__spec-label">Tim tường</dt>
              <dd className="bedroom-modal__spec-value">{formatArea(apt.timTuong)} m²</dd>
            </div>
            <div className="bedroom-modal__spec-row bedroom-modal__spec-row--area">
              <dt className="bedroom-modal__spec-label">Thông thủy</dt>
              <dd className="bedroom-modal__spec-value">{formatArea(apt.thongThuy)} m²</dd>
            </div>
          </dl>

        </div>

        {/* Image column — right on desktop, above info on mobile */}
        <div className="bedroom-modal__img-wrap">
          <img
            src={config.image}
            alt={`Mặt bằng ${config.label}`}
            className="bedroom-modal__img"
            draggable={false}
          />
        </div>
      </div>
    </div>
  )
}

export default BedroomModal
